import { PER_OPEN } from "../queryCachePolicy";
import type { AccessToken } from "../types";
import type { components } from "../types/voislog_api";
import { voislogApi } from "../utils/apiClients/voislogApi";
import type { NotificationSettingDraft } from "../components/molecules/NotificationSetting/notificationSettingDraft";

/** 通知の受信設定のクエリキー。ユーザー別キャッシュの無効化は先頭要素のみ指定する */
export const NOTIFICATION_SETTING_KEY = "notificationSetting";

type Schemas = components["schemas"];

type SettingQuery<T> = {
  queryKey: (string | number)[];
  queryFn: () => Promise<T>;
} & typeof PER_OPEN;

export type NotificationSettingQueries = [
  SettingQuery<Schemas["AlertSettingsResponseWithDefault"]>,
  SettingQuery<Schemas["ReminderSettingsResponseWithDefault"]>,
  SettingQuery<Schemas["ReportSettingsResponseWithDefault"]>,
  SettingQuery<Schemas["EmailDeliverySettingResponseWithDefault"]>,
];

// 通知の受信設定画面で読む4つ。本人の解析通知・未測定・月間レポートの設定（user_id を付けなければ本人が対象）と、
// 会社のメール配信設定。openedAt は画面を開いた時刻。開くたびに別のキーにして読み直す
export function notificationSettingQueries(
  tokenInfo: AccessToken | null,
  openedAt: number,
): NotificationSettingQueries {
  const queryKey = (name: string): (string | number)[] => [
    NOTIFICATION_SETTING_KEY,
    name,
    tokenInfo?.access_token ?? "",
    openedAt,
  ];
  return [
    {
      queryKey: queryKey("alert"),
      queryFn: async () => {
        const { data } = await voislogApi.GET(
          "/v2/core/notifications/mail/alert-setting",
        );
        return data!;
      },
      ...PER_OPEN,
    },
    {
      queryKey: queryKey("reminder"),
      queryFn: async () => {
        const { data } = await voislogApi.GET(
          "/v2/core/notifications/mail/reminder-setting",
        );
        return data!;
      },
      ...PER_OPEN,
    },
    {
      queryKey: queryKey("report"),
      queryFn: async () => {
        const { data } = await voislogApi.GET(
          "/v2/core/notifications/mail/report-setting",
        );
        return data!;
      },
      ...PER_OPEN,
    },
    {
      queryKey: queryKey("emailDelivery"),
      queryFn: async () => {
        const { data } = await voislogApi.GET(
          "/v2/core/notifications/mail/email-delivery-setting",
          { params: { query: { company_id: tokenInfo!.company_id } } },
        );
        return data!;
      },
      ...PER_OPEN,
    },
  ];
}

const ALERT_SETTING = "/v2/core/notifications/mail/alert-setting";
const REMINDER_SETTING = "/v2/core/notifications/mail/reminder-setting";
const REPORT_SETTING = "/v2/core/notifications/mail/report-setting";

// 3つの設定を同時に保存する。企業の値に従っている設定は自分の設定を消し（DELETE）、そうでなければ値を送る（PUT）。
// user_id を付けなければ本人が対象。3つとも終わるのを待ち、1つでも失敗していたら最初の失敗を投げる
export async function saveNotificationSetting(
  draft: NotificationSettingDraft,
): Promise<void> {
  const { alert, reminder, report } = draft;
  const results = await Promise.allSettled([
    alert.followsCompany
      ? voislogApi.DELETE(ALERT_SETTING)
      : voislogApi.PUT(ALERT_SETTING, { body: { setting_data: alert.value } }),
    reminder.followsCompany
      ? voislogApi.DELETE(REMINDER_SETTING)
      : voislogApi.PUT(REMINDER_SETTING, {
          body: { setting_data: reminder.value },
        }),
    report.followsCompany
      ? voislogApi.DELETE(REPORT_SETTING)
      : voislogApi.PUT(REPORT_SETTING, {
          body: { setting_data: report.value },
        }),
  ]);
  const failed = results.find((r) => r.status === "rejected");
  if (failed) throw failed.reason;
}
